import React from 'react';

import { TRAINING_CALL_COPY } from '../../constants/training';
import type { SessionCompletedData } from '../../services/playbookLiveWebSocketService';
import { Button } from '../ui/button';

interface PlaybookLiveCompletionModalProps {
  summary: SessionCompletedData;
  /** Same destination as the knowledge-base end screen: the admin portal's Training page. */
  onViewResults: () => void;
}

/** "S3_POSITIONING" -> "positioning". */
const stageName = (stage?: string): string => (stage ?? '').replace(/^S\d_/, '').toLowerCase();

/** End of a Playbook Live session, over the live-call layout it ran in. */
export const PlaybookLiveCompletionModal: React.FC<PlaybookLiveCompletionModalProps> = ({ summary, onViewResults }) => {
  const closeSignals = summary?.closeSignals ?? [];
  const isDropped = summary?.endedEarly === true;
  return (
    <div className="modal-backdrop">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="playbook-live-complete-title"
        className="modal-panel p-6 max-w-[560px] w-full mx-4"
      >
        <h2 id="playbook-live-complete-title" className="heading-2 mb-3">
          {isDropped ? TRAINING_CALL_COPY.connectionDroppedTitle : 'Session ended'}
        </h2>
        {isDropped && (
          <p role="alert" className="text-sm text-ink-secondary leading-relaxed mb-2">
            {TRAINING_CALL_COPY.connectionDropped}
          </p>
        )}
        <p className="text-sm text-ink-secondary leading-relaxed">
          You handled <strong>{summary?.itemsCompleted ?? 0}</strong> of <strong>{summary?.itemCount ?? 0}</strong> of
          the customer&apos;s concerns, and the conversation reached <strong>{stageName(summary?.finalStage)}</strong>.
        </p>
        {closeSignals.length > 0 && (
          <p className="text-sm text-ink-secondary leading-relaxed">
            They gave you {closeSignals.length} opening{closeSignals.length === 1 ? '' : 's'} to close:{' '}
            {closeSignals
              .map((signal) => TRAINING_CALL_COPY.closeSignalOutcome[signal?.outcome ?? ''] ?? signal?.outcome)
              .join(', ')}
            .
          </p>
        )}
        {/* Same wording as the knowledge-base session's end screen. */}
        <p className="text-[13px] text-ink-muted leading-relaxed">
          Your session has been saved. Deep analysis will be ready shortly.
        </p>
        <div className="mt-4">
          <Button type="button" variant="primary" onClick={onViewResults}>
            {TRAINING_CALL_COPY.viewResults}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PlaybookLiveCompletionModal;
